export interface CanopyNode {
	day: number;
	canopy: number;
}

export interface CanopyCycle {
	start: number;
	end: number;
}

/**
 * Lettuce / alfalfa seed formulas plus node interpolation.
 * Prototype only — no persistence.
 */
export class CanopyCurveProto {
	static readonly CANOPY_A = 6.2;
	static readonly CANOPY_B = -11.5;
	static readonly GMAX = 80;
	static readonly MAX_FRACTION = 0.98;
	static readonly ALFALFA_A = 4;
	static readonly ALFALFA_B = -13.876;
	static readonly ALFALFA_MIN = 20;
	static readonly ALFALFA_NI = 35;
	static readonly NODE_EPSILON = 1.5;

	static daysBetween(a: Date, b: Date): number {
		return Math.round((b.getTime() - a.getTime()) / 86400000);
	}

	static addDays(d: Date, days: number): Date {
		return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
	}

	static toInputDate(d: Date): string {
		let m = ('0' + (d.getMonth() + 1)).slice(-2);
		let day = ('0' + d.getDate()).slice(-2);

		return d.getFullYear() + '-' + m + '-' + day;
	}

	static fromInputDate(s: string): Date {
		let p = (s || '').split('-');

		if (p.length !== 3) {
			return null;
		}

		return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
	}

	static cycles(cuts: number[], maxDay: number): CanopyCycle[] {
		let bounds = [0].concat((cuts || []).slice().sort((a, b) => a - b)).concat([maxDay + 1]);
		let result: CanopyCycle[] = [];
		let i: number;

		for (i = 0; i < bounds.length - 1; i++) {
			result.push({ start: bounds[i], end: bounds[i + 1] - 1 });
		}

		return result;
	}

	static previousCut(day: number, cuts: number[]): number {
		let prev = 0;

		(cuts || []).forEach(c => {
			if (c <= day) {
				prev = c;
			}
		});

		return prev;
	}

	static isCycleBoundary(day: number, cuts: number[], maxDay: number): boolean {
		if (day === 0 || day === maxDay) {
			return true;
		}

		return (cuts || []).some(c => c === day || c - 1 === day);
	}

	static seedCanopy(day: number, maxDay: number, crop = 'lettuce', cuts: number[] = []): number {
		let t: number;
		let range: number;

		if (crop !== 'alfalfa') {
			return this.GMAX / (1 + Math.exp(this.CANOPY_A + this.CANOPY_B *
				((day + 1) / ((maxDay + 1) * this.MAX_FRACTION))));
		}

		t = (day - this.previousCut(day, cuts)) / this.ALFALFA_NI;
		if (t > 1) {
			t = 1;
		}

		range = this.GMAX - this.ALFALFA_MIN;
		return range / (1 + Math.exp(this.ALFALFA_A + this.ALFALFA_B * t)) + this.ALFALFA_MIN;
	}

	static dailySeed(maxDay: number, crop = 'lettuce', cuts: number[] = []): CanopyNode[] {
		let points: CanopyNode[] = [];
		let d: number;

		for (d = 0; d <= maxDay; d++) {
			points.push({ day: d, canopy: this.seedCanopy(d, maxDay, crop, cuts) });
		}

		return points;
	}

	static simplifyNodes(points: CanopyNode[], epsilon = this.NODE_EPSILON): CanopyNode[] {
		if (!points || points.length <= 2) {
			return this.copyNodes(points || []);
		}

		return this._simplifyRange(points, 0, points.length - 1, epsilon);
	}

	static nodesFromDaily(daily: CanopyNode[], cuts: number[], maxDay: number): CanopyNode[] {
		let nodes: CanopyNode[] = [];

		this.cycles(cuts, maxDay).forEach(cycle => {
			let slice = daily.filter(p => p.day >= cycle.start && p.day <= cycle.end);
			nodes = nodes.concat(this.simplifyNodes(slice));
		});

		return nodes;
	}

	private static _simplifyRange(points: CanopyNode[], i0: number, i1: number,
		epsilon: number): CanopyNode[] {
		let maxErr = 0;
		let maxI = i0;
		let i: number;
		let a = points[i0];
		let b = points[i1];
		let t: number;
		let lerp: number;
		let err: number;
		let left: CanopyNode[];
		let right: CanopyNode[];

		if (i1 <= i0 + 1 || b.day === a.day) {
			return [{ day: a.day, canopy: a.canopy }, { day: b.day, canopy: b.canopy }];
		}

		for (i = i0 + 1; i < i1; i++) {
			t = (points[i].day - a.day) / (b.day - a.day);
			lerp = a.canopy + t * (b.canopy - a.canopy);
			err = Math.abs(points[i].canopy - lerp);
			if (err > maxErr) {
				maxErr = err;
				maxI = i;
			}
		}

		if (maxErr <= epsilon) {
			return [{ day: a.day, canopy: a.canopy }, { day: b.day, canopy: b.canopy }];
		}

		left = this._simplifyRange(points, i0, maxI, epsilon);
		right = this._simplifyRange(points, maxI, i1, epsilon);
		return left.concat(right.slice(1));
	}

	static sampleSeedNodes(maxDay: number, crop = 'lettuce', cuts: number[] = []): CanopyNode[] {
		return this.nodesFromDaily(this.dailySeed(maxDay, crop, cuts), cuts, maxDay);
	}

	/** Fake satellite points: follow the seed with noise. Lettuce gets a late dip. */
	static fakeNdvi(maxDay: number, crop = 'lettuce', cuts: number[] = []): CanopyNode[] {
		let points: CanopyNode[] = [];
		let day = 4;
		let seed: number;
		let jitter: number;
		let canopy: number;

		while (day < maxDay) {
			seed = this.seedCanopy(day, maxDay, crop, cuts);
			jitter = ((day * 17) % 19) - 9;
			canopy = seed + jitter + 4;
			if (crop !== 'alfalfa' && day > maxDay * 0.7) {
				canopy -= (day - maxDay * 0.7) * 1.4;
			}
			points.push({ day: day, canopy: this.clamp(canopy, 0, 100) });
			day += 5 + (day % 4);
		}

		return points;
	}

	static splitAtCuts(points: CanopyNode[], cuts: number[]): CanopyNode[][] {
		let segs: CanopyNode[][] = [];
		let cur: CanopyNode[] = [];

		if (!cuts || cuts.length === 0) {
			return [points];
		}

		points.forEach(p => {
			if (cur.length && cuts.indexOf(p.day) >= 0) {
				segs.push(cur);
				cur = [];
			}
			cur.push(p);
		});

		if (cur.length) {
			segs.push(cur);
		}

		return segs;
	}

	static logisticCanopy(day: number, maxDay: number, gmax: number, a: number, b: number): number {
		let x = (day + 1) / ((maxDay + 1) * this.MAX_FRACTION);
		return gmax / (1 + Math.exp(a + b * x));
	}

	static ndviFitDaily(ndvi: CanopyNode[], maxDay: number, crop = 'lettuce',
		cuts: number[] = []): CanopyNode[] {
		let all: CanopyNode[] = [];
		let minFloor = 0;

		if (!cuts || cuts.length === 0) {
			return this.ndviFitInterval(ndvi, maxDay, minFloor);
		}

		this.cycles(cuts, maxDay).forEach(cycle => {
			let localMax = cycle.end - cycle.start;
			let localNdvi = ndvi.filter(p => p.day >= cycle.start && p.day <= cycle.end)
				.map(p => ({ day: p.day - cycle.start, canopy: p.canopy }));
			let fitted: CanopyNode[];

			if (localNdvi.length < 3) {
				fitted = this.dailySeed(localMax, crop, []);
			} else {
				fitted = this.ndviFitInterval(localNdvi, localMax, minFloor);
			}

			fitted.forEach(p => {
				all.push({
					day: p.day + cycle.start,
					canopy: this.clamp(p.canopy, minFloor, 100)
				});
			});
		});

		return all;
	}

	static ndviFitInterval(ndvi: CanopyNode[], maxDay: number, minFloor = 0): CanopyNode[] {
		let params = this.fitLogistic(ndvi, maxDay);
		let dipDay = this.detectDipStart(ndvi, maxDay, params);
		let preDip: CanopyNode[];
		let tail: CanopyNode[] = [];
		let daily: CanopyNode[] = [];
		let d: number;
		let last: CanopyNode;
		let value: number;

		if (dipDay !== null) {
			preDip = ndvi.filter(p => p.day < dipDay);
			if (preDip.length >= 3) {
				params = this.fitLogistic(preDip, maxDay);
			}

			tail = [{
				day: dipDay,
				canopy: this.logisticCanopy(dipDay, maxDay, params.gmax, params.a, params.b)
			}];
			ndvi.forEach(p => {
				if (p.day >= dipDay) {
					tail.push({ day: p.day, canopy: p.canopy });
				}
			});
			last = tail[tail.length - 1];
			if (last.day < maxDay) {
				tail.push({ day: maxDay, canopy: last.canopy });
			}
		}

		for (d = 0; d <= maxDay; d++) {
			value = (dipDay === null || d <= dipDay)
				? this.logisticCanopy(d, maxDay, params.gmax, params.a, params.b)
				: this.spline(tail, d);
			daily.push({ day: d, canopy: this.clamp(value, minFloor, 100) });
		}

		return daily;
	}

	private static fitLogistic(ndvi: CanopyNode[], maxDay: number): { gmax: number, a: number, b: number } {
		let best = { gmax: this.GMAX, a: this.CANOPY_A, b: this.CANOPY_B };
		let bestErr = Number.POSITIVE_INFINITY;
		let gmax: number;
		let a: number;
		let b: number;
		let err: number;

		for (gmax = 55; gmax <= 95; gmax += 2.5) {
			for (a = 2; a <= 9; a += 0.5) {
				for (b = -18; b <= -5; b += 0.5) {
					err = this._sse(ndvi, maxDay, gmax, a, b);
					if (err < bestErr) {
						bestErr = err;
						best = { gmax: gmax, a: a, b: b };
					}
				}
			}
		}

		return best;
	}

	private static _sse(ndvi: CanopyNode[], maxDay: number, gmax: number, a: number, b: number): number {
		let sum = 0;
		let diff: number;

		ndvi.forEach(p => {
			diff = this.logisticCanopy(p.day, maxDay, gmax, a, b) - p.canopy;
			sum += diff * diff;
		});

		return sum;
	}

	private static detectDipStart(ndvi: CanopyNode[], maxDay: number,
		params: { gmax: number, a: number, b: number }): number {
		const DIP = 12;
		let run = 0;
		let start: number = null;
		let i: number;
		let p: CanopyNode;
		let y: number;

		for (i = 0; i < ndvi.length; i++) {
			p = ndvi[i];
			y = this.logisticCanopy(p.day, maxDay, params.gmax, params.a, params.b);

			if ((y >= params.gmax * 0.55 || p.day >= maxDay * 0.5) && p.canopy < y - DIP) {
				if (run === 0) {
					start = p.day;
				}
				run++;
				if (run >= 2) {
					return start;
				}
			} else {
				run = 0;
				start = null;
			}
		}

		return null;
	}

	static spline(nodes: CanopyNode[], day: number): number {
		let i: number;
		let a: CanopyNode;
		let b: CanopyNode;
		let m: number[];
		let h: number;
		let t: number;
		let t2: number;
		let t3: number;

		if (!nodes || nodes.length === 0) {
			return 0;
		}

		if (nodes.length === 1 || day <= nodes[0].day) {
			return nodes[0].canopy;
		}

		if (day >= nodes[nodes.length - 1].day) {
			return nodes[nodes.length - 1].canopy;
		}

		m = this._tangents(nodes);

		for (i = 0; i < nodes.length - 1; i++) {
			a = nodes[i];
			b = nodes[i + 1];

			if (day < a.day || day > b.day) {
				continue;
			}

			h = b.day - a.day;
			if (h === 0) {
				return a.canopy;
			}

			t = (day - a.day) / h;
			t2 = t * t;
			t3 = t2 * t;

			return this.clamp(
				(2 * t3 - 3 * t2 + 1) * a.canopy +
				(t3 - 2 * t2 + t) * h * m[i] +
				(-2 * t3 + 3 * t2) * b.canopy +
				(t3 - t2) * h * m[i + 1],
				0, 100);
		}

		return nodes[nodes.length - 1].canopy;
	}

	private static _tangents(nodes: CanopyNode[]): number[] {
		let n = nodes.length;
		let delta: number[] = [];
		let m: number[] = [];
		let i: number;
		let a: number;
		let b: number;
		let s: number;
		let h: number;

		if (n < 2) {
			return [0];
		}

		for (i = 0; i < n - 1; i++) {
			h = nodes[i + 1].day - nodes[i].day;
			delta.push(h === 0 ? 0 : (nodes[i + 1].canopy - nodes[i].canopy) / h);
		}

		m[0] = delta[0];
		m[n - 1] = delta[n - 2];

		for (i = 1; i < n - 1; i++) {
			m[i] = (delta[i - 1] * delta[i] <= 0) ? 0 : (delta[i - 1] + delta[i]) / 2;
		}

		for (i = 0; i < n - 1; i++) {
			if (delta[i] === 0) {
				m[i] = 0;
				m[i + 1] = 0;
				continue;
			}

			a = m[i] / delta[i];
			b = m[i + 1] / delta[i];
			s = a * a + b * b;
			if (s > 9) {
				s = 3 / Math.sqrt(s);
				m[i] = s * a * delta[i];
				m[i + 1] = s * b * delta[i];
			}
		}

		return m;
	}

	/** Monotone cubic through nodes, split at cuts so harvest drops stay a step. */
	static splineCanopy(nodes: CanopyNode[], day: number, cuts: number[] = [],
		maxDay = 0): number {
		let cycle = this.cycles(cuts, maxDay).filter(c => day >= c.start && day <= c.end)[0];
		let local: CanopyNode[];

		if (!cycle) {
			return this.spline(nodes, day);
		}

		local = nodes.filter(n => n.day >= cycle.start && n.day <= cycle.end);
		if (!local.length) {
			return this.spline(nodes, day);
		}

		return this.spline(local, day);
	}

	static dailySpline(nodes: CanopyNode[], maxDay: number, cuts: number[] = []): CanopyNode[] {
		let points: CanopyNode[] = [];
		let d: number;

		if (!nodes || !nodes.length) {
			return points;
		}

		for (d = 0; d <= maxDay; d++) {
			points.push({
				day: d,
				canopy: this.splineCanopy(nodes, d, cuts, maxDay)
			});
		}

		return points;
	}

	static interpolate(nodes: CanopyNode[], day: number): number {
		let i: number;
		let a: CanopyNode;
		let b: CanopyNode;
		let t: number;

		if (!nodes || nodes.length === 0) {
			return 0;
		}

		if (day <= nodes[0].day) {
			return nodes[0].canopy;
		}

		if (day >= nodes[nodes.length - 1].day) {
			return nodes[nodes.length - 1].canopy;
		}

		for (i = 0; i < nodes.length - 1; i++) {
			a = nodes[i];
			b = nodes[i + 1];

			if (day >= a.day && day <= b.day) {
				if (b.day === a.day) {
					return a.canopy;
				}

				t = (day - a.day) / (b.day - a.day);
				return a.canopy + t * (b.canopy - a.canopy);
			}
		}

		return nodes[nodes.length - 1].canopy;
	}

	static copyNodes(nodes: CanopyNode[]): CanopyNode[] {
		return nodes.map(n => ({ day: n.day, canopy: n.canopy }));
	}

	static clamp(value: number, min: number, max: number): number {
		return Math.min(max, Math.max(min, value));
	}
}
